import React, { useState, useEffect } from "react";
import { getFolders, Folder } from "../../api/folders";
import { handleError } from "../../utils/handleError";
import {
  ArrowDownRight,
  CornerDownRight,
  Folder as FolderIcon,
} from "react-feather";

interface FolderSelectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectFolder: (folderPath: string) => void;
  currentFolder?: string;
  title?: string;
  excludeFolder?: string; // Folder path to exclude (and its subdirectories)
}

export function FolderSelectModal({
  isOpen,
  onClose,
  onSelectFolder,
  currentFolder = "",
  title = "Move Files to Folder",
  excludeFolder,
}: FolderSelectModalProps) {
  const [folders, setFolders] = useState<Folder[]>([]);
  const [currentPath, setCurrentPath] = useState("");
  const [breadcrumbs, setBreadcrumbs] = useState<
    { name: string; path: string }[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadFolders("");
    }
  }, [isOpen]);

  const loadFolders = async (parentPath: string) => {
    try {
      setIsLoading(true);
      const folderData = await getFolders(parentPath);
      setFolders(folderData);
      setCurrentPath(parentPath);

      // Update breadcrumbs
      if (!parentPath) {
        setBreadcrumbs([]);
      } else {
        const pathParts = parentPath.split("/");
        const breadcrumbItems = pathParts.map((part, index) => ({
          name: part,
          path: pathParts.slice(0, index + 1).join("/"),
        }));
        setBreadcrumbs(breadcrumbItems);
      }
    } catch (error) {
      handleError(error as Error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFolderClick = (folder: Folder) => {
    loadFolders(folder.path);
  };

  const handleSelectFolder = (folderPath: string) => {
    onSelectFolder(folderPath);
    onClose();
  };

  const handleBreadcrumbClick = (path: string) => {
    loadFolders(path);
  };

  if (!isOpen) return null;

  return (
    <div className="modal modal-open">
      <div className="modal-box max-w-2xl">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-bold text-lg">{title}</h3>
          <button className="btn btn-sm btn-circle btn-ghost" onClick={onClose}>
            ✕
          </button>
        </div>

        {/* Breadcrumb Navigation */}
        <div className="mb-4">
          <div className="breadcrumbs text-sm">
            <ul>
              <li>
                <button
                  className="btn btn-ghost btn-xs"
                  onClick={() => loadFolders("")}
                >
                  <FolderIcon size={14} />
                </button>
              </li>
              {breadcrumbs.map((item) => (
                <li key={item.path}>
                  <button
                    className="btn btn-ghost btn-xs"
                    onClick={() => handleBreadcrumbClick(item.path)}
                  >
                    {item.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Folder List */}
        <div className="max-h-96 overflow-y-auto">
          {isLoading ? (
            <div className="flex justify-center py-8">
              <span className="loading loading-spinner loading-lg"></span>
            </div>
          ) : folders.length === 0 ? (
            <div className="text-center py-8 text-base-content/60">
              No folders in this location
            </div>
          ) : (
            <div className="space-y-2">
              {folders
                .filter((folder) => {
                  // Exclude the folder being moved and its subdirectories
                  if (excludeFolder) {
                    return (
                      folder.path !== excludeFolder &&
                      !folder.path.startsWith(excludeFolder + "/")
                    );
                  }
                  return true;
                })
                .map((folder) => {
                  const isDisabled =
                    folder.path === currentFolder ||
                    (excludeFolder
                      ? folder.path === excludeFolder ||
                        folder.path.startsWith(excludeFolder + "/")
                      : false);

                  return (
                    <div
                      key={folder.id}
                      className="flex items-center justify-between bg-base-200 rounded-lg hover:bg-base-300 transition-colors"
                    >
                      <div
                        className="flex flex-1 items-center p-3 gap-3 cursor-pointer"
                        onClick={() => handleFolderClick(folder)}
                      >
                        <span className="text-xl">
                          <FolderIcon size={16} />
                        </span>
                        <span className="font-medium">{folder.name}</span>
                      </div>
                      <div className="flex gap-2 p-3">
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => handleSelectFolder(folder.path)}
                          disabled={isDisabled}
                        >
                          <CornerDownRight size={16} /> Move
                        </button>
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
        </div>

        <div className="modal-action items-center justify-between">
          <button className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-primary btn-sm"
            onClick={() => handleSelectFolder(currentPath)}
            disabled={
              currentPath === currentFolder ||
              (excludeFolder
                ? currentPath === excludeFolder ||
                  currentPath.startsWith(excludeFolder + "/")
                : false)
            }
          >
            <CornerDownRight size={16} />{" "}
            {currentPath ? `Move to "${currentPath}"` : "Move to Root"}
          </button>
        </div>
      </div>
    </div>
  );
}
